import React from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    ArrowForward as BackIcon,
    Search as SearchIcon,
    Save as SaveIcon,
    Lock as LockIcon,
    CalendarToday as CalendarIcon,
    CheckCircle as CheckIcon,
    Description as ReportIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { styles } from '../../styles/ChemoRecordsStyles';
import { useChemoReportForm, convertToBoolean, formatDateForInput } from '../../hooks/chemo/useChemoReportForm';
import { chemoTranslations } from '../../hooks/chemo/chemoTranslations';

const ChemoReportForm = ({
    report,
    onSubmit,
    onCancel,
    hospital,
    fetchedRecords = [],
    isLoadingRecords = false,
    error = null,
    handleCheckboxChange,
    handleFieldChange,
    formatDate,
    onSave
}) => {
    const {
        formData,
        warningDialog,
        dateWarningDialog,
        handleFormChange,
        handleSubmit,
        handleSave,
        handleWarningClose,
        handleDateWarningClose,
        handleSaveAnyway,
        handleSaveWithDifferentDates,
        isRecordLocked,
        getPreferredDueDate,
        getUnchangedRecordsMessage,
        getFormTitle
    } = useChemoReportForm(report, hospital, fetchedRecords, onSubmit, onSave);

    return (
        <Box sx={styles.formContainer}>
            <Paper sx={styles.formPaper}>
                <Box sx={styles.formHeader}>
                    <IconButton
                        onClick={onCancel}
                        sx={{
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.15)'
                            }
                        }}
                    >
                        <BackIcon />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <ReportIcon sx={{ fontSize: 28 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {getFormTitle()}
                        </Typography>
                    </Box>
                    <Box sx={{ width: 40 }} />
                </Box>

                <Box sx={{ padding: { xs: '16px', sm: '24px', md: '32px' } }}>
                    <Box sx={{
                        marginBottom: { xs: '24px', md: '32px' },
                        padding: { xs: '16px', sm: '20px', md: '24px' },
                        backgroundColor: '#f8fafb',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid #e3f2fd'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            marginBottom: { xs: '16px', md: '24px' },
                            paddingBottom: '16px',
                            borderBottom: '1px solid #e3f2fd',
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <CalendarIcon sx={{ color: '#5e72e4', fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                <Typography variant="h6" sx={{
                                    fontWeight: 600,
                                    color: '#37474f',
                                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                                }}>
                                    {chemoTranslations.form.reportParams}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={handleSubmit}
                                disabled={isLoadingRecords}
                                fullWidth={false}
                                sx={{
                                    backgroundColor: '#5e72e4',
                                    fontWeight: 600,
                                    padding: { xs: '8px 20px', sm: '9px 22px', md: '10px 24px' },
                                    borderRadius: { xs: '8px', md: '10px' },
                                    boxShadow: '0 4px 12px rgba(94, 114, 228, 0.25)',
                                    textTransform: 'none',
                                    fontSize: { xs: '14px', md: '15px' },
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': {
                                        backgroundColor: '#4c63d2',
                                        boxShadow: '0 6px 16px rgba(94, 114, 228, 0.3)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#cfd8dc'
                                    }
                                }}
                            >
                                {chemoTranslations.form.searchReport}
                            </Button>
                        </Box>

                        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label={chemoTranslations.form.startDate}
                                    type="date"
                                    value={formData.startDate}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleFormChange('startDate', e.target.value)}
                                    sx={styles.formTextField}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label={chemoTranslations.form.endDate}
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => handleFormChange('endDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={styles.formTextField}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={chemoTranslations.form.hospital}
                                    value={formData.hospital}
                                    InputProps={{ readOnly: true }}
                                    sx={styles.formTextFieldReadOnly}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {isLoadingRecords ? (
                        <LoadingState />
                    ) : error ? (
                        <ErrorState error={error} />
                    ) : fetchedRecords.length > 0 ? (
                        <RecordsSection
                            fetchedRecords={fetchedRecords}
                            isRecordLocked={isRecordLocked}
                            getPreferredDueDate={getPreferredDueDate}
                            handleCheckboxChange={handleCheckboxChange}
                            handleFieldChange={handleFieldChange}
                            formatDate={formatDate}
                            handleSave={handleSave}
                            isLoadingRecords={isLoadingRecords}
                        />
                    ) : (
                        <EmptyState />
                    )}
                </Box>
            </Paper>

            <WarningDialog
                open={warningDialog.open}
                unmodifiedRecords={warningDialog.unmodifiedRecords}
                onClose={handleWarningClose}
                onSaveAnyway={handleSaveAnyway}
                getUnchangedRecordsMessage={getUnchangedRecordsMessage}
            />

            <DateWarningDialog
                open={dateWarningDialog.open}
                differentDates={dateWarningDialog.differentDates}
                onClose={handleDateWarningClose}
                onContinue={handleSaveWithDifferentDates}
            />
        </Box>
    );
};

const LoadingState = () => (
    <Box sx={{
        marginTop: { xs: '24px', md: '32px' },
        textAlign: 'center',
        padding: { xs: '40px 16px', sm: '52px 24px', md: '64px' },
        backgroundColor: '#f8fafb',
        borderRadius: { xs: '8px', md: '12px' },
        border: '2px dashed #e3f2fd'
    }}>
        <CircularProgress
            thickness={4}
            sx={{
                color: '#5e72e4',
                marginBottom: { xs: '16px', md: '20px' },
                width: { xs: '40px !important', sm: '45px !important', md: '50px !important' },
                height: { xs: '40px !important', sm: '45px !important', md: '50px !important' }
            }}
        />
        <Typography variant="h6" sx={{
            color: '#546e7a',
            fontWeight: 600,
            marginTop: '12px',
            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
        }}>
            {chemoTranslations.form.loadingData}
        </Typography>
        <Typography variant="body2" sx={{
            color: '#90a4ae',
            marginTop: '8px',
            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' }
        }}>
            {chemoTranslations.form.pleaseWait}
        </Typography>
    </Box>
);

const ErrorState = ({ error }) => (
    <Box sx={{
        marginTop: { xs: '24px', md: '32px' },
        textAlign: 'center',
        padding: { xs: '40px 16px', sm: '52px 24px', md: '64px' },
        backgroundColor: '#ffebee',
        borderRadius: { xs: '8px', md: '12px' },
        border: '2px solid #ef5350'
    }}>
        <Typography variant="h6" sx={{
            color: '#c62828',
            fontWeight: 600,
            marginBottom: '12px',
            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
        }}>
            {chemoTranslations.errors.errorOccurred}
        </Typography>
        <Typography variant="body1" sx={{
            color: '#d32f2f',
            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' }
        }}>
            {error}
        </Typography>
    </Box>
);

const EmptyState = () => (
    <Box sx={styles.emptyState}>
        <Box sx={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#f8fafb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px',
            border: '2px solid #e3f2fd'
        }}>
            <SearchIcon sx={{ fontSize: 40, color: '#90a4ae' }} />
        </Box>
        <Typography variant="h6" sx={{ color: '#546e7a', fontWeight: 600 }}>
            {chemoTranslations.form.noDataToDisplay}
        </Typography>
        <Typography variant="body2" sx={{ color: '#90a4ae' }}>
            {chemoTranslations.form.useFormToSearch}
        </Typography>
    </Box>
);

const RecordsSection = ({
    fetchedRecords,
    isRecordLocked,
    getPreferredDueDate,
    handleCheckboxChange,
    handleFieldChange,
    formatDate,
    handleSave,
    isLoadingRecords
}) => (
    <Box sx={{ marginTop: { xs: '24px', md: '32px' } }}>
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            marginBottom: { xs: '16px', md: '20px' },
            padding: { xs: '12px 16px', sm: '14px 18px', md: '16px 20px' },
            backgroundColor: '#5e72e4',
            borderRadius: { xs: '8px', md: '10px' },
            boxShadow: '0 2px 8px rgba(94, 114, 228, 0.2)',
            gap: { xs: 2, sm: 0 }
        }}>
            <Typography variant="h6" sx={{
                color: '#fff',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '8px', md: '12px' },
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
            }}>
                <CheckIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                {chemoTranslations.form.treatmentRecords}
            </Typography>
            <Chip
                label={`${fetchedRecords.length} ${chemoTranslations.labels.records}`}
                sx={{
                    backgroundColor: '#fff',
                    color: '#5e72e4',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                    padding: '4px 8px',
                    height: { xs: '28px', md: '32px' },
                    width: { xs: '100%', sm: 'auto' }
                }}
            />
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <DesktopTable
                fetchedRecords={fetchedRecords}
                isRecordLocked={isRecordLocked}
                getPreferredDueDate={getPreferredDueDate}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
                formatDate={formatDate}
            />
        </Box>

        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <MobileCards
                fetchedRecords={fetchedRecords}
                isRecordLocked={isRecordLocked}
                getPreferredDueDate={getPreferredDueDate}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
                formatDate={formatDate}
            />
        </Box>

        <Box sx={{
            ...styles.formActions,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 }
        }}>
            <Button
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isLoadingRecords}
                fullWidth={false}
                sx={{
                    backgroundColor: '#66bb6a',
                    minWidth: { xs: '100%', sm: '220px' },
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    fontWeight: 600,
                    padding: { xs: '12px 32px', sm: '14px 40px' },
                    borderRadius: { xs: '8px', md: '10px' },
                    boxShadow: '0 4px 12px rgba(102, 187, 106, 0.3)',
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: '#4caf50',
                        boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)'
                    },
                    '&:disabled': {
                        backgroundColor: '#cfd8dc',
                        boxShadow: 'none'
                    }
                }}
            >
                {chemoTranslations.form.saveReport}
            </Button>
        </Box>
    </Box>
);

const DesktopTable = ({
    fetchedRecords,
    isRecordLocked,
    getPreferredDueDate,
    handleCheckboxChange,
    handleFieldChange,
    formatDate
}) => (
    <TableContainer
        component={Paper}
        sx={{
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            borderRadius: { xs: '8px', md: '12px' },
            border: '1px solid #e3f2fd',
            overflow: 'hidden'
        }}
    >
        <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafb' }}>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.row}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.deliveryDate}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.day}</TableCell>
                    <TableCell align="right" sx={{ ...styles.tableHeaderCell, direction: 'rtl', textAlign: 'right' }}>{chemoTranslations.table.districtDescription}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.customerNumber}</TableCell>
                    <TableCell align="right" sx={{ ...styles.tableHeaderCell, direction: 'rtl', textAlign: 'right' }}>{chemoTranslations.table.customerName}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.willArrive}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.willNotArrive}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.endTreatment}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.updateDate}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.order}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.remainingDoses}</TableCell>
                    <TableCell align="center" sx={styles.tableHeaderCell}>{chemoTranslations.table.lastDate}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {fetchedRecords.map((record, index) => {
                    const lockStatus = isRecordLocked(record);
                    const preferredDueDate = getPreferredDueDate(record);

                    return (
                        <TableRow
                            key={record.ORDI1 || index}
                            sx={{
                                '&:nth-of-type(odd)': { backgroundColor: '#fafbfc' },
                                '&:nth-of-type(even)': { backgroundColor: '#fff' },
                                '&:hover': { backgroundColor: '#e3f2fd' },
                                opacity: lockStatus.locked ? 0.5 : 1,
                                position: 'relative',
                                transition: 'all 0.2s ease',
                                borderLeft: lockStatus.locked ? '3px solid #ef5350' : '3px solid transparent'
                            }}
                        >
                            <TableCell align="center" sx={styles.tableCell}>
                                {lockStatus.locked && (
                                    <Tooltip title={lockStatus.reason} arrow>
                                        <LockIcon sx={{ fontSize: '1rem', color: '#ef5350', marginLeft: '6px', verticalAlign: 'middle' }} />
                                    </Tooltip>
                                )}
                                {record.KLINE2}
                            </TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{formatDate(preferredDueDate)}</TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{record.DAY}</TableCell>
                            <TableCell align="right" sx={{ ...styles.tableCell, direction: 'rtl', textAlign: 'right', maxWidth: '200px' }}>{record.CUSTDES}</TableCell>
                            <TableCell align="center" sx={{ ...styles.tableCell, color: '#5e72e4', fontWeight: 600 }}>{record.CUSTNAME}</TableCell>
                            <TableCell align="right" sx={{ ...styles.tableCell, direction: 'rtl', textAlign: 'right' }}>{record.CDES}</TableCell>
                            <TableCell align="center" sx={{ padding: '8px 4px' }}>
                                <Tooltip title={lockStatus.locked ? lockStatus.reason : chemoTranslations.table.markWillArrive} arrow>
                                    <span>
                                        <Checkbox
                                            checked={convertToBoolean(record.APPROVED)}
                                            onChange={() => !lockStatus.locked && handleCheckboxChange(index, 'APPROVED')}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            sx={{ color: '#66bb6a', '&.Mui-checked': { color: '#43a047' } }}
                                        />
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ padding: '8px 4px' }}>
                                <Tooltip title={lockStatus.locked ? lockStatus.reason : chemoTranslations.table.markWillNotArrive} arrow>
                                    <span>
                                        <Checkbox
                                            checked={convertToBoolean(record.NOTAPPROVED)}
                                            onChange={() => !lockStatus.locked && handleCheckboxChange(index, 'NOTAPPROVED')}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            sx={{ color: '#ef5350', '&.Mui-checked': { color: '#e53935' } }}
                                        />
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ padding: '8px 4px' }}>
                                <Tooltip title={lockStatus.locked ? lockStatus.reason : chemoTranslations.table.markEndTreatment} arrow>
                                    <span>
                                        <Checkbox
                                            checked={convertToBoolean(record.FINISH)}
                                            onChange={() => !lockStatus.locked && handleCheckboxChange(index, 'FINISH')}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            sx={{ color: '#5e72e4', '&.Mui-checked': { color: '#4c63d2' } }}
                                        />
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ padding: '8px 4px' }}>
                                <Tooltip title={lockStatus.locked ? lockStatus.reason : chemoTranslations.table.updateDateTooltip} arrow>
                                    <span>
                                        <TextField
                                            type="date"
                                            value={formatDateForInput(record.TODUEDATE) || ''}
                                            onChange={(e) => !lockStatus.locked && handleFieldChange(index, 'TODUEDATE', e.target.value)}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            InputLabelProps={{ shrink: true }}
                                            sx={styles.tableDateField}
                                        />
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{record.ORDNAME}</TableCell>
                            <TableCell align="center" sx={{ ...styles.tableCell, fontWeight: 600, color: '#5e72e4' }}>{record.ORDBAL}</TableCell>
                            <TableCell align="center" sx={styles.tableCell}>{formatDate(record.LASTDUEDATE)}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </TableContainer>
);

const MobileCards = ({
    fetchedRecords,
    isRecordLocked,
    getPreferredDueDate,
    handleCheckboxChange,
    handleFieldChange,
    formatDate
}) => (
    <>
        {fetchedRecords.map((record, index) => {
            const lockStatus = isRecordLocked(record);
            const preferredDueDate = getPreferredDueDate(record);

            return (
                <Paper
                    key={index}
                    sx={{
                        padding: { xs: 2, sm: 2.5 },
                        marginBottom: 2,
                        backgroundColor: '#fff',
                        borderRadius: { xs: '8px', md: '10px' },
                        border: lockStatus.locked ? '2px solid #ef5350' : '1px solid #e3f2fd',
                        opacity: lockStatus.locked ? 0.7 : 1,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                    }}
                >
                    {lockStatus.locked && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            marginBottom: 2,
                            padding: 1.5,
                            backgroundColor: '#ffebee',
                            borderRadius: '6px'
                        }}>
                            <LockIcon sx={{ color: '#ef5350', fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                                {lockStatus.reason}
                            </Typography>
                        </Box>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.row}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#5e72e4' }}>
                                {record.KLINE2}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.deliveryDate}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(preferredDueDate)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.day}
                            </Typography>
                            <Typography variant="body2">{record.DAY}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.customerNumber}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5e72e4' }}>
                                {record.CUSTNAME}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.districtDescription}
                            </Typography>
                            <Typography variant="body2">{record.CUSTDES}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.customerName}
                            </Typography>
                            <Typography variant="body2">{record.CDES}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{
                                padding: 1.5,
                                backgroundColor: '#f8fafb',
                                borderRadius: '6px',
                                marginTop: 1
                            }}>
                                <Typography variant="caption" sx={{
                                    fontWeight: 600,
                                    color: '#546e7a',
                                    display: 'block',
                                    marginBottom: 1
                                }}>
                                    {chemoTranslations.table.status}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">{chemoTranslations.table.willArrive}</Typography>
                                        <Checkbox
                                            checked={convertToBoolean(record.APPROVED)}
                                            onChange={() => !lockStatus.locked && handleCheckboxChange(index, 'APPROVED')}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            sx={{ color: '#66bb6a', '&.Mui-checked': { color: '#43a047' } }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">{chemoTranslations.table.willNotArrive}</Typography>
                                        <Checkbox
                                            checked={convertToBoolean(record.NOTAPPROVED)}
                                            onChange={() => !lockStatus.locked && handleCheckboxChange(index, 'NOTAPPROVED')}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            sx={{ color: '#ef5350', '&.Mui-checked': { color: '#e53935' } }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">{chemoTranslations.table.endTreatment}</Typography>
                                        <Checkbox
                                            checked={convertToBoolean(record.FINISH)}
                                            onChange={() => !lockStatus.locked && handleCheckboxChange(index, 'FINISH')}
                                            size="small"
                                            disabled={lockStatus.locked}
                                            sx={{ color: '#5e72e4', '&.Mui-checked': { color: '#4c63d2' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" sx={{
                                fontSize: '0.7rem',
                                display: 'block',
                                marginBottom: 0.5
                            }}>
                                {chemoTranslations.table.updateDeliveryDate}
                            </Typography>
                            <TextField
                                type="date"
                                value={formatDateForInput(record.TODUEDATE) || ''}
                                onChange={(e) => !lockStatus.locked && handleFieldChange(index, 'TODUEDATE', e.target.value)}
                                size="small"
                                disabled={lockStatus.locked}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        backgroundColor: lockStatus.locked ? '#eceff1' : '#fff',
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.order}
                            </Typography>
                            <Typography variant="body2">{record.ORDNAME}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.remainingDoses}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#5e72e4' }}>
                                {record.ORDBAL}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                {chemoTranslations.table.lastDate}
                            </Typography>
                            <Typography variant="body2">{formatDate(record.LASTDUEDATE)}</Typography>
                        </Grid>
                    </Grid>
                </Paper>
            );
        })}
    </>
);

const WarningDialog = ({ open, unmodifiedRecords, onClose, onSaveAnyway, getUnchangedRecordsMessage }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
            '& .MuiDialog-paper': {
                margin: { xs: 2, sm: 3 },
                width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' }
            }
        }}
        PaperProps={{
            sx: {
                borderRadius: { xs: '8px', md: '12px' },
                padding: { xs: '4px', sm: '8px' }
            }
        }}
    >
        <DialogTitle sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 1.5 },
            color: '#f57c00',
            fontWeight: 600,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
            paddingBottom: '8px',
            padding: { xs: 2, sm: 3 }
        }}>
            <WarningIcon sx={{ fontSize: { xs: 28, sm: 30, md: 32 }, color: '#ff9800' }} />
            {chemoTranslations.warnings.unchangedRecordsTitle}
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <DialogContentText sx={{
                color: '#546e7a',
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                marginBottom: '16px'
            }}>
                {getUnchangedRecordsMessage(unmodifiedRecords.length)}
            </DialogContentText>
            <Box sx={{
                backgroundColor: '#fff3e0',
                borderRadius: { xs: '6px', md: '8px' },
                padding: { xs: 1.5, sm: 2 },
                border: '1px solid #ffb74d',
                maxHeight: { xs: '200px', sm: '300px' },
                overflowY: 'auto'
            }}>
                {unmodifiedRecords.map((record, index) => (
                    <Box
                        key={index}
                        sx={{
                            padding: { xs: '6px 10px', sm: '8px 12px' },
                            marginBottom: index < unmodifiedRecords.length - 1 ? '8px' : 0,
                            backgroundColor: '#ffffff',
                            borderRadius: '6px',
                            border: '1px solid #ffe0b2',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <WarningIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#ff9800' }} />
                        <Typography sx={{
                            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                            fontWeight: 500,
                            color: '#37474f'
                        }}>
                            {record.CUSTNAME || record.PARTNAME}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Typography sx={{
                color: '#78909c',
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                marginTop: '16px',
                fontStyle: 'italic'
            }}>
                {chemoTranslations.warnings.selectOptionForEachRecord}
            </Typography>
        </DialogContent>
        <DialogActions sx={{
            padding: { xs: '12px 16px', sm: '16px 24px' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
        }}>
            <Button
                onClick={onClose}
                variant="outlined"
                fullWidth={false}
                sx={{
                    color: '#5e72e4',
                    borderColor: '#5e72e4',
                    textTransform: 'none',
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                        borderColor: '#324cdd',
                        backgroundColor: 'rgba(94, 114, 228, 0.04)'
                    }
                }}
            >
                {chemoTranslations.warnings.backToEdit}
            </Button>
            <Button
                onClick={onSaveAnyway}
                variant="contained"
                fullWidth={false}
                sx={{
                    backgroundColor: '#ff9800',
                    textTransform: 'none',
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                        backgroundColor: '#f57c00'
                    }
                }}
            >
                {chemoTranslations.warnings.saveAnyway}
            </Button>
        </DialogActions>
    </Dialog>
);

const DateWarningDialog = ({ open, differentDates, onClose, onContinue }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
            '& .MuiDialog-paper': {
                margin: { xs: 2, sm: 3 },
                width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' }
            }
        }}
    >
        <DialogTitle sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 1.5 },
            color: '#e65100',
            fontWeight: 600,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
            paddingBottom: '8px',
            padding: { xs: 2, sm: 3 }
        }}>
            <WarningIcon sx={{ fontSize: { xs: 28, sm: 30, md: 32 }, color: '#ff6f00' }} />
            {chemoTranslations.warnings.differentDatesTitle}
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <DialogContentText sx={{
                color: '#546e7a',
                fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                marginBottom: '16px'
            }}>
                {chemoTranslations.warnings.differentDatesMessage}
            </DialogContentText>
            <Box sx={{
                backgroundColor: '#fff3e0',
                borderRadius: { xs: '6px', md: '8px' },
                padding: { xs: 1.5, sm: 2 },
                border: '1px solid #ffb74d'
            }}>
                <Typography sx={{
                    fontWeight: 600,
                    marginBottom: '12px',
                    color: '#37474f',
                    fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' }
                }}>
                    {chemoTranslations.warnings.deliveryDatesFound}
                </Typography>
                {differentDates.map((date, index) => (
                    <Box
                        key={index}
                        sx={{
                            padding: { xs: '6px 10px', sm: '8px 12px' },
                            marginBottom: index < differentDates.length - 1 ? '8px' : 0,
                            backgroundColor: '#ffffff',
                            borderRadius: '6px',
                            border: '1px solid #ffe0b2',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <CalendarIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#ff9800' }} />
                        <Typography sx={{
                            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                            fontWeight: 500,
                            color: '#37474f'
                        }}>
                            {date}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Typography sx={{
                color: '#78909c',
                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                marginTop: '16px',
                fontStyle: 'italic'
            }}>
                {chemoTranslations.warnings.recommendSameDate}
            </Typography>
        </DialogContent>
        <DialogActions sx={{
            padding: { xs: '12px 16px', sm: '16px 24px' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
        }}>
            <Button
                onClick={onClose}
                variant="outlined"
                fullWidth={false}
                sx={{
                    color: '#5e72e4',
                    borderColor: '#5e72e4',
                    textTransform: 'none',
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                        borderColor: '#324cdd',
                        backgroundColor: 'rgba(94, 114, 228, 0.04)'
                    }
                }}
            >
                {chemoTranslations.warnings.backToEdit}
            </Button>
            <Button
                onClick={onContinue}
                variant="contained"
                fullWidth={false}
                sx={{
                    backgroundColor: '#ff9800',
                    textTransform: 'none',
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                        backgroundColor: '#f57c00'
                    }
                }}
            >
                {chemoTranslations.warnings.continueAnyway}
            </Button>
        </DialogActions>
    </Dialog>
);

export default ChemoReportForm;
